import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { ApiError, absUrl, api, qs } from '../lib/api.js';
import { useFetch } from '../lib/useFetch.js';
import type { FeedbackItem, FeedbackListResponse, FeedbackStatus } from '../lib/types.js';
import { StatusChip, StatusFilterChip } from '../components/StatusChip.js';
import { formatTs } from '../lib/format.js';

const STATUSES: FeedbackStatus[] = ['new', 'triaged', 'planned', 'done', 'declined'];
const CATEGORIES = ['bug', 'balance', 'idea', 'performance', 'other', 'pulse'];
const SEARCH_DEBOUNCE_MS = 250;

export function Feedback() {
  const [status, setStatus] = useState<FeedbackStatus | ''>('');
  const [category, setCategory] = useState('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setQ(qInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [qInput]);

  const fetchList = useCallback(
    () =>
      api.get<FeedbackListResponse>(
        `/api/admin/feedback${qs({ status: status || undefined, category: category || undefined, q: q || undefined, page })}`,
      ),
    [status, category, q, page],
  );
  const list = useFetch(fetchList, [status, category, q, page]);
  const items = list.data?.items ?? [];

  useEffect(() => {
    if (items.length === 0) return;
    if (selectedId === null || !items.some((it) => it.id === selectedId)) {
      setSelectedId(items[0]!.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const selectedIndex = items.findIndex((it) => it.id === selectedId);
  const selected = selectedIndex >= 0 ? items[selectedIndex] : undefined;

  // j/k keyboard navigation through the list, ignored while typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (items.length === 0) return;
      if (e.key === 'j') {
        e.preventDefault();
        const idx = Math.min(items.length - 1, (selectedIndex < 0 ? -1 : selectedIndex) + 1);
        setSelectedId(items[idx]!.id);
      } else if (e.key === 'k') {
        e.preventDefault();
        const idx = Math.max(0, (selectedIndex < 0 ? 1 : selectedIndex) - 1);
        setSelectedId(items[idx]!.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items, selectedIndex]);

  return (
    <div>
      <h1 class="page-title">Feedback inbox</h1>
      {list.error && <div class="error-banner">{list.error}</div>}

      <div class="row" style={{ marginBottom: '12px' }}>
        <StatusFilterChip
          status=""
          label="All"
          active={status === ''}
          onClick={() => setStatus('')}
        />
        {STATUSES.map((s) => (
          <StatusFilterChip
            key={s}
            status={s}
            label={s}
            active={status === s}
            onClick={() => setStatus(s)}
          />
        ))}
        <select
          class="select"
          style={{ width: 140 }}
          value={category}
          onChange={(e) => setCategory((e.target as HTMLSelectElement).value)}
        >
          <option value="">All categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <input
          class="input"
          style={{ width: 200 }}
          placeholder="Search text…"
          value={qInput}
          onInput={(e) => {
            setPage(1);
            setQInput((e.target as HTMLInputElement).value);
          }}
        />
        <span class="hint">j / k to move · {list.data?.total ?? 0} total</span>
      </div>

      <div class="feedback-shell">
        <div class="feedback-list">
          {list.loading && items.length === 0 && <div class="empty">Loading…</div>}
          {!list.loading && items.length === 0 && (
            <div class="empty">No feedback matches these filters.</div>
          )}
          {items.map((it) => (
            <div
              key={it.id}
              class={`feedback-row${it.id === selectedId ? ' selected' : ''}`}
              onClick={() => setSelectedId(it.id)}
            >
              <StatusChip status={it.status} />
              <span class="who">{it.playerName}</span>
              <span class="text">{it.text}</span>
              <span class="rating">{'★'.repeat(Math.max(0, Math.min(5, it.rating)))}</span>
            </div>
          ))}
          <div class="row" style={{ marginTop: '10px' }}>
            <button
              class="btn btn-sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span class="hint">page {page}</span>
            <button
              class="btn btn-sm"
              disabled={items.length === 0 || page * items.length >= (list.data?.total ?? 0)}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>

        {selected && <FeedbackDrawer key={selected.id} item={selected} onChanged={list.reload} />}
      </div>
    </div>
  );
}

function FeedbackDrawer({ item, onChanged }: { item: FeedbackItem; onChanged: () => void }) {
  const [note, setNote] = useState(item.adminNote ?? '');
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const contextJson = useMemo(() => {
    try {
      return JSON.stringify(item.context, null, 2);
    } catch {
      return String(item.context);
    }
  }, [item.context]);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setMsg(null);
    try {
      await fn();
      setMsg(`${label} saved.`);
      onChanged();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : `${label} failed.`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div class="drawer">
      <h3>
        #{item.id} · {item.playerName}
      </h3>
      <div class="hint">
        {formatTs(item.ts)} · {item.category} · {'★'.repeat(item.rating)}
      </div>

      <div class="section-label">Status</div>
      <select
        class="select"
        value={item.status}
        onChange={(e) =>
          run('Status', () =>
            api.patch(`/api/admin/feedback/${item.id}`, {
              status: (e.target as HTMLSelectElement).value,
            }),
          )
        }
      >
        {STATUSES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>

      <div class="section-label">Message</div>
      <div class="card" style={{ padding: '10px 12px', fontSize: '13px' }}>
        {item.text}
      </div>

      <div class="section-label">Context</div>
      <pre>{contextJson}</pre>

      {item.screenshotUrl && (
        <>
          <div class="section-label">Screenshot</div>
          <img
            class="screenshot"
            src={absUrl(item.screenshotUrl)}
            alt="Player-submitted screenshot"
            loading="lazy"
          />
        </>
      )}

      <div class="section-label">Admin note</div>
      <textarea
        class="textarea"
        value={note}
        onInput={(e) => setNote((e.target as HTMLTextAreaElement).value)}
      />
      <button
        class="btn btn-sm"
        style={{ marginTop: '6px' }}
        disabled={busy !== null}
        onClick={() =>
          run('Note', () => api.patch(`/api/admin/feedback/${item.id}`, { adminNote: note }))
        }
      >
        Save note
      </button>

      <div class="section-label">Reply to player</div>
      {item.replyText && (
        <div class="hint" style={{ marginBottom: '6px' }}>
          Last reply: {item.replyText}
        </div>
      )}
      <textarea
        class="textarea"
        placeholder="Reply…"
        value={reply}
        onInput={(e) => setReply((e.target as HTMLTextAreaElement).value)}
      />
      <div class="row" style={{ marginTop: '6px' }}>
        <button
          class="btn btn-sm"
          disabled={busy !== null || reply.trim() === ''}
          onClick={() =>
            run('Reply', async () => {
              await api.post(`/api/admin/feedback/${item.id}/reply`, { text: reply });
              setReply('');
            })
          }
        >
          Send reply
        </button>
        <button
          class="btn btn-sm btn-primary"
          disabled={busy !== null}
          onClick={() => run('Promote', () => api.post(`/api/admin/feedback/${item.id}/promote`))}
        >
          Promote to roadmap
        </button>
      </div>

      {msg && (
        <div class="hint" style={{ marginTop: '8px' }}>
          {msg}
        </div>
      )}
    </div>
  );
}
